import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { GT_COLORS } from '../groups-teams/shared/constants';
import { NotificationRulesService } from '../../services/notification-rules.service';
import { ALERT_TYPE_LABEL, RoutingOverview } from '../../types/notification-rules.types';
import { joinList, leaderRoutedTypes } from './routing-copy';

/**
 * "What this person receives", shown next to the notification-method
 * checkboxes on a system user.
 *
 * Without it an admin sees four checkboxes with no way to know whether this
 * person is on the hook for anything, which is how a team ends up with a
 * leader nobody can reach. Generated from the same rules the alert pipeline
 * uses, so it stays true as routing changes.
 */
export const RoutingForUserNote: React.FC<{ userId: string | null }> = ({ userId }) => {
    const [routing, setRouting] = useState<RoutingOverview | null>(null);

    useEffect(() => {
        let cancelled = false;
        NotificationRulesService.fetchOverview()
            .then((o) => { if (!cancelled) setRouting(o); })
            .catch(() => { /* the form works without the explanation */ });
        return () => { cancelled = true; };
    }, []);

    if (!routing) return null;

    const routed = leaderRoutedTypes(routing.rules).map((r) => ALERT_TYPE_LABEL[r.alertType]);
    const isPointOfContact = Boolean(userId && routing.pointOfContact?.userId === userId);
    const namedFor = routing.rules
        .filter((r) => userId && r.named.some((p) => p.userId === userId))
        .map((r) => ALERT_TYPE_LABEL[r.alertType]);

    const lines: string[] = [];
    if (routed.length) {
        lines.push(
            `If this person leads a team, they receive ${joinList(routed)} alerts for that team's POM Users.`,
        );
    } else {
        lines.push('No alert type currently routes to team leaders, so leading a team sends this person nothing.');
    }
    if (namedFor.length) {
        lines.push(`They are a named recipient for ${joinList(namedFor)}, whatever team the alert comes from.`);
    }
    if (isPointOfContact) {
        lines.push('They are the organization point of contact, so they are notified whenever an alert would otherwise reach no one.');
    }

    return (
        <Box
            sx={{
                border: `1px solid ${GT_COLORS.border}`,
                backgroundColor: GT_COLORS.headerBg,
                borderRadius: '8px',
                padding: '10px 12px',
                mb: 2,
                display: 'grid',
                gap: '4px',
            }}
        >
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textSecondary }}>
                What this person receives
            </Typography>
            {lines.map((line) => (
                <Typography key={line} sx={{ fontSize: '12px', color: GT_COLORS.textMuted, lineHeight: 1.5 }}>
                    {line}
                </Typography>
            ))}
            <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted, lineHeight: 1.5 }}>
                With no method selected they are skipped, and the alert falls to{' '}
                {routing.backstop.kind === 'point-of-contact' && routing.backstop.person
                    ? `${routing.backstop.person.name}, the organization point of contact.`
                    : routing.backstop.kind === 'admin-fallback'
                        ? `all ${routing.backstop.adminCount} reachable admins.`
                        : 'no one.'}
            </Typography>
        </Box>
    );
};
